// requestLifecycle
// -----------------
// The pure, typed client-side state machine for a request's overall status —
// the mirror of the db-8 BEFORE UPDATE transition guard, in TypeScript.
//
// Status is 100% system-derived; this reducer is the ONLY way the client should
// change it, so by construction it can never produce an illegal transition.
// It encodes the same boundary rules as db-8 + the dbToReq draft-fix:
//
//   • A Draft only ever becomes Submitted (via SUBMIT). Nothing else changes a
//     Draft's status — in particular DERIVE never runs over a Draft (a draft's
//     items have no approval decisions, so deriving would wrongly yield
//     "Submitted").
//   • A submitted/decided request never returns to Draft (no action produces it).
//   • Otherwise the status is the pure function of the children's per-item
//     approval statuses (deriveOverallStatus), which only ever returns members of
//     the closed post-submission set {Submitted, Part Processed, Fully
//     Processed} — the legitimate approve↔decline churn, both directions.
//     (D-1, 26 June 2026: the old terminal Approved/Declined collapsed into the
//     single "Fully Processed".)
//
// Terminal-state locking of "Fully Processed" (D-2: once fully processed, a
// request is locked — no reopen/reverse, D-4) IS now enforced in the DERIVE
// branch (Slice 2): a CONFIRMED Fully Processed request (one whose
// approvalsConfirmedAt is set, threaded in as the DERIVE action's `confirmed`
// flag) is never re-derived downward. The lock is keyed on confirmation, NOT the
// bare derived status, so pre-confirm staging is not frozen. This mirrors the
// db-8 guard "no transition away from Fully Processed" (the authoritative
// enforcement) + the UI lock (Revert/per-item edit disabled when confirmed).
// See docs/status-model.md §3 + docs/handover-2026-06-26-status-model.md.
//
// TypeScript source — requestLifecycle.js is a thin re-export stub.

import type { ChildRow } from "../types/database.js";
import type { RequestStatus } from "../utils/enums";
import { REQUEST_STATUSES } from "../utils/enums";
import { deriveOverallStatus } from "./approvals";

export interface LifecycleState {
  status: RequestStatus | null;
}

export type LifecycleAction =
  // Initialise from a loaded request (status already resolved by dbToReq —
  // trusted as-is, including a legitimately re-derived value).
  | { type: "LOAD"; status: RequestStatus | null }
  // Shelter submits a Draft. Only valid from Draft; a no-op otherwise.
  | { type: "SUBMIT" }
  // Re-derive status from the children's per-item approval statuses. Covers
  // every staff action that changes a decision (per-item approve/decline,
  // confirm & send, revert). No-op over a Draft; keeps current status if there
  // are no children to derive from.
  //
  // `confirmed` carries whether the request is an already-CONFIRMED (terminal)
  // request — true when its persisted `approvalsConfirmedAt` is set. It is the
  // signal for the D-2/D-4 terminal lock below: a confirmed `Fully Processed`
  // request is never re-derived downward. It must NOT be the bare derived status,
  // because pre-confirm staging can reach `Fully Processed` in-memory while still
  // unconfirmed — locking on that would freeze legitimate staging churn.
  | { type: "DERIVE"; children?: ChildRow[] | null; confirmed?: boolean };

export function requestLifecycleReducer(
  state: LifecycleState,
  action: LifecycleAction
): LifecycleState {
  switch (action.type) {
    case "LOAD":
      return { status: action.status };

    case "SUBMIT":
      // A Draft (and only a Draft) advances to Submitted.
      if (state.status === REQUEST_STATUSES.DRAFT) {
        return { status: REQUEST_STATUSES.SUBMITTED };
      }
      return state; // no-op for anything already submitted

    case "DERIVE": {
      // Never derive over a Draft — its items carry no decisions yet.
      if (state.status === REQUEST_STATUSES.DRAFT) return state;
      // Terminal lock (D-2/D-4, Slice 2): a CONFIRMED Fully Processed request is
      // locked — never re-derive it downward. Keyed on `confirmed`, NOT on the
      // bare status, so pre-confirm staging (which can reach Fully Processed
      // in-memory with approvalsConfirmedAt === null) is NOT frozen — the
      // responder may still revert/adjust until Confirm & Send. This mirrors the
      // db-8 guard "no transition away from Fully Processed".
      if (state.status === REQUEST_STATUSES.FULLY_PROCESSED && action.confirmed) {
        return state;
      }
      const derived = deriveOverallStatus({ children: action.children || [] });
      // deriveOverallStatus returns null only when there are no items at all;
      // in that case keep the current status rather than blanking it.
      return derived ? { status: derived } : state;
    }

    default:
      return state;
  }
}

/**
 * The status to persist when a request is submitted. Routes the Draft→Submitted
 * transition through the guarded reducer's SUBMIT action: a Draft — or a brand-new
 * request, which has no prior status and is treated as a Draft — advances to
 * Submitted; anything already submitted or decided is returned unchanged (never
 * re-submitted, never knocked back to Draft). This is the single client-side entry
 * point for the submit transition, shared by the request wizard and the portal
 * save/update-draft paths so they can't diverge.
 */
export function nextStatusOnSubmit(currentStatus?: RequestStatus | null): RequestStatus | null {
  return requestLifecycleReducer(
    { status: currentStatus ?? REQUEST_STATUSES.DRAFT },
    { type: "SUBMIT" }
  ).status;
}
